import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatDate as formatDateUtil, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogBody } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, 
  Edit, 
  FolderOpen, 
  FileText, 
  Plus,
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  MessageSquare,
  ClipboardList,
  Building,
  DollarSign,
  MapPinned,
  Eye,
  EyeOff,
  AlertCircle,
  XCircle,
  CheckCircle,
  Trash2,
  Activity,
  UserPlus,
  Edit3,
  Upload,
  UserCheck,
  RefreshCw,
  FolderPlus,
  FolderMinus,
  ChevronRight,
  Accessibility,
  AlertTriangle,
  Ban,
  ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import CommunicationsPanel from '../components/CommunicationsPanel';
import ExpirationAlertModal from '../components/ExpirationAlertModal';
import PendingIdentificationAlertModal from '../components/PendingIdentificationAlertModal';
import { SsnIncompleteFlag } from '../components/SsnIncompleteFlag';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can delete participants
const DELETE_ROLES = ['system_admin', 'executive', 'operations_manager'];

// Mapping from enrollment type base names to contract program types
// This handles cases where naming conventions differ between systems
const ENROLLMENT_TO_CONTRACT_PROGRAM_MAP = {
  'food_pantry': 'food_assistance',
  'snap_et': 'snap_et',
  'snap_outreach': 'snap_outreach',
  'addictions': 'recovery_services',
  'housing': 'housing_services',
  'employment': 'employment_training',
  'mental_health': 'mental_health',
  'crisis': 'crisis_services',
  'transportation': 'transportation',
  'childcare': 'childcare'
};

const ParticipantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [participant, setParticipant] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [cases, setCases] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Get navigation state for context preservation
  const navigationState = location.state || {};
  const defaultTab = navigationState.activeTab || 'profile';
  const [loading, setLoading] = useState(true);
  
  // SSN reveal state
  const [ssnRevealed, setSSNRevealed] = useState(false);
  const [revealedSSN, setRevealedSSN] = useState(null);
  const [revealingSSN, setRevealingSSN] = useState(false);
  
  // Enrollment dialog state
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [programTypes, setProgramTypes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [states, setStates] = useState([]);
  const [fundingSources, setFundingSources] = useState([]);
  const [enrollmentForm, setEnrollmentForm] = useState({
    program_type: '',
    state: '',
    contract_name: '',
    funding_source: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });
  const [enrolling, setEnrolling] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  // Case creation dialog state
  const [showCaseDialog, setShowCaseDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [allowedServiceTypes, setAllowedServiceTypes] = useState([]);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
  const [caseForm, setCaseForm] = useState({
    service_type: '',
    priority: 'medium',
    target_outcomes: '',
    notes: ''
  });
  const [creatingCase, setCreatingCase] = useState(false);

  // Enrollment management state
  const [showEditEnrollmentDialog, setShowEditEnrollmentDialog] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [enrollmentEditForm, setEnrollmentEditForm] = useState({
    program_type: '',
    state: '',
    contract_name: '',
    funding_source: '',
    assigned_staff_id: '',
    notes: '',
    start_date: '',
    end_date: ''
  });
  const [savingEnrollment, setSavingEnrollment] = useState(false);
  const [deletingEnrollment, setDeletingEnrollment] = useState(false);
  const [closingEnrollment, setClosingEnrollment] = useState(false);

  // Ban participant state
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banning, setBanning] = useState(false);
  const [unbanning, setUnbanning] = useState(false);

  // Participant delete state
  const [showDeleteParticipantDialog, setShowDeleteParticipantDialog] = useState(false);
  const [deletingParticipant, setDeletingParticipant] = useState(false);
  
  // SNAP E&T Expiration Alert state
  const [showExpirationAlerts, setShowExpirationAlerts] = useState(false);
  const [expirationAlertsChecked, setExpirationAlertsChecked] = useState(false);
  
  // Pending Identification Verification Alert state
  const [showPendingIdAlert, setShowPendingIdAlert] = useState(false);
  const [pendingIdAlertChecked, setPendingIdAlertChecked] = useState(false);
  
  // Check if user can delete participants
  const canDelete = DELETE_ROLES.includes(user?.role);

  useEffect(() => {
    fetchParticipantData();
    fetchMetadata();
    fetchActivities();
  }, [id]);

  // Check for SNAP E&T expiration alerts when participant data loads
  useEffect(() => {
    const checkExpirationAlerts = async () => {
      if (!participant || expirationAlertsChecked) return;
      
      try {
        const response = await axios.get(`${API}/participants/${id}/expiration-alerts`);
        const alerts = response.data.alerts || [];
        
        if (alerts.length > 0) {
          setShowExpirationAlerts(true);
        }
        setExpirationAlertsChecked(true);
      } catch (error) {
        console.error('Failed to check expiration alerts:', error);
        setExpirationAlertsChecked(true);
      }
    };
    
    checkExpirationAlerts();
  }, [participant, id, expirationAlertsChecked]);

  // Check for Pending Identification Verification alerts when participant data loads
  useEffect(() => {
    const checkPendingIdAlert = async () => {
      if (!participant || pendingIdAlertChecked) return;
      
      try {
        const response = await axios.get(`${API}/participants/${id}/pending-id-verification-alert`);
        
        if (response.data.has_alert) {
          setShowPendingIdAlert(true);
        }
        setPendingIdAlertChecked(true);
      } catch (error) {
        console.error('Failed to check pending ID verification alert:', error);
        setPendingIdAlertChecked(true);
      }
    };
    
    checkPendingIdAlert();
  }, [participant, id, pendingIdAlertChecked]);

  const fetchParticipantData = async () => {
    try {
      const [participantRes, enrollmentsRes, casesRes, notesRes] = await Promise.all([
        axios.get(`${API}/participants/${id}`),
        axios.get(`${API}/participants/${id}/enrollments`),
        axios.get(`${API}/participants/${id}/cases`),
        axios.get(`${API}/case-notes?participant_id=${id}`)
      ]);
      setParticipant(participantRes.data);
      setEnrollments(enrollmentsRes.data.enrollments || []);
      setCases(casesRes.data.items || []);
      setNotes(notesRes.data.items || []);
    } catch (error) {
      toast.error('Failed to load participant');
      navigate('/participants');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const response = await axios.get(`${API}/participants/${id}/activity?limit=30`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [enrollmentTypesRes, statesRes, contractsRes, serviceTypesRes, fundingSourcesRes] = await Promise.all([
        axios.get(`${API}/enrollment-types`),
        axios.get(`${API}/states`),
        axios.get(`${API}/contracts`),
        axios.get(`${API}/case-service-types`),
        axios.get(`${API}/admin/funding-sources`)
      ]);
      // Use enrollment types as program types
      setProgramTypes(enrollmentTypesRes.data.enrollment_types || []);
      setStates(statesRes.data);
      setContracts(contractsRes.data);
      setServiceTypes(serviceTypesRes.data);
      setFundingSources(fundingSourcesRes.data.funding_sources || []);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  // Fetch enrollment defaults when enrollment type or start date changes
  const fetchEnrollmentDefaults = async (enrollmentTypeId, startDate) => {
    if (!enrollmentTypeId) return;
    
    setLoadingDefaults(true);
    try {
      const response = await axios.get(`${API}/enrollment-defaults/${enrollmentTypeId}?start_date=${startDate}`);
      const defaults = response.data;
      
      // Auto-populate contract from resolved pattern (contract_id/contract_name)
      // If contract_id is resolved, use the contract name; otherwise leave blank
      const resolvedContractName = defaults.contract_name || '';
      
      // Auto-populate funding source from enrollment type settings
      const resolvedFundingSource = defaults.funding_source_name || '';
      
      setEnrollmentForm(prev => ({
        ...prev,
        end_date: defaults.end_date || '',
        contract_name: resolvedContractName,
        funding_source: resolvedFundingSource,
        state: defaults.state || ''
      }));
    } catch (error) {
      console.error('Failed to fetch enrollment defaults:', error);
    } finally {
      setLoadingDefaults(false);
    }
  };

  const handleEnrollmentTypeChange = (value) => {
    setEnrollmentForm(prev => ({ ...prev, program_type: value }));
    fetchEnrollmentDefaults(value, enrollmentForm.start_date);
  };

  const handleStartDateChange = (value) => {
    setEnrollmentForm(prev => ({ ...prev, start_date: value }));
    if (enrollmentForm.program_type) {
      fetchEnrollmentDefaults(enrollmentForm.program_type, value);
    }
  };

  const handleCreateEnrollment = async () => {
    if (!enrollmentForm.program_type) {
      toast.error('Please select an enrollment type');
      return;
    }

    // Get the state from the enrollment type
    const enrollmentType = programTypes.find(pt => pt.id === enrollmentForm.program_type);
    if (!enrollmentType) {
      toast.error('Invalid enrollment type');
      return;
    }

    setEnrolling(true);
    try {
      const response = await axios.post(`${API}/participants/${id}/enrollments`, {
        participant_id: id,
        program_type: enrollmentForm.program_type,
        state: enrollmentType.state, // Use state from enrollment type
        contract_name: enrollmentForm.contract_name || '',
        funding_source: enrollmentForm.funding_source || '',
        start_date: enrollmentForm.start_date,
        end_date: enrollmentForm.end_date || null
      });
      
      // Show success message about auto-created case
      if (response.data.auto_created_case) {
        toast.success(`Enrollment created! Case ${response.data.auto_created_case.id} auto-created.`);
      } else {
        toast.success('Enrollment created successfully');
      }
      
      setShowEnrollDialog(false);
      setEnrollmentForm({
        program_type: '',
        state: '',
        contract_name: '',
        funding_source: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      fetchParticipantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create enrollment');
    } finally {
      setEnrolling(false);
    }
  };

  const handleCreateCase = async () => {
    if (!selectedEnrollment || !caseForm.service_type) {
      toast.error('Please select an enrollment and service type');
      return;
    }

    setCreatingCase(true);
    try {
      const response = await axios.post(
        `${API}/participants/${id}/enrollments/${selectedEnrollment.id}/cases`,
        caseForm
      );
      toast.success('Case created successfully');
      setShowCaseDialog(false);
      setCaseForm({
        service_type: '',
        priority: 'medium',
        target_outcomes: '',
        notes: ''
      });
      
      // Check if this is a SNAP E&T case to auto-show targeted outcomes
      const isSnapEt = selectedEnrollment.program_type?.toLowerCase().includes('snap') && 
                       selectedEnrollment.program_type?.toLowerCase().includes('e&t');
      
      setSelectedEnrollment(null);
      setAllowedServiceTypes([]);
      
      // Navigate to case page with state to trigger targeted outcomes dialog
      navigate(`/cases/${response.data.id}`, { 
        state: { showTargetedOutcomes: isSnapEt }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    } finally {
      setCreatingCase(false);
    }
  };

  const openCaseDialog = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    setShowCaseDialog(true);
    setCaseForm(prev => ({ ...prev, service_type: '' }));
    
    // Fetch allowed service types for this program
    setLoadingServiceTypes(true);
    try {
      const response = await axios.get(`${API}/case-service-types?program_type=${enrollment.program_type}`);
      setAllowedServiceTypes(response.data);
      // Auto-select if only one option
      if (response.data.length === 1) {
        setCaseForm(prev => ({ ...prev, service_type: response.data[0].id }));
      }
    } catch (error) {
      toast.error('Failed to load service types');
      setAllowedServiceTypes(serviceTypes); // Fallback to all service types
    } finally {
      setLoadingServiceTypes(false);
    }
  };

  const handleRevealSSN = async () => {
    setRevealingSSN(true);
    try {
      const response = await axios.get(`${API}/participants/${id}/reveal-ssn`);
      setRevealedSSN(response.data.ssn);
      setSSNRevealed(true);
      toast.success('SSN revealed - this access has been logged');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You are not authorized to view the full SSN');
      } else {
        toast.error('Failed to reveal SSN');
      }
    } finally {
      setRevealingSSN(false);
    }
  };

  const handleHideSSN = () => {
    setSSNRevealed(false);
    setRevealedSSN(null);
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Enrollment management handlers
  const openEditEnrollmentDialog = (enrollment) => {
    setEditingEnrollment(enrollment);
    // Format dates for input[type="date"] (needs YYYY-MM-DD format)
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      // If already in YYYY-MM-DD format, return as is
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
      // Parse and format
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      // Use UTC date parts to avoid timezone shift
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEnrollmentEditForm({
      program_type: enrollment.program_type || '',
      state: enrollment.state || '',
      contract_name: enrollment.contract_name || '',
      funding_source: enrollment.funding_source || '',
      assigned_staff_id: enrollment.assigned_staff_id || '',
      notes: enrollment.notes || '',
      start_date: formatDateForInput(enrollment.start_date),
      end_date: formatDateForInput(enrollment.end_date)
    });
    setShowEditEnrollmentDialog(true);
  };

  const handleUpdateEnrollment = async () => {
    if (!editingEnrollment) return;
    
    setSavingEnrollment(true);
    try {
      await axios.put(
        `${API}/participants/${id}/enrollments/${editingEnrollment.id}`,
        enrollmentEditForm
      );
      toast.success('Enrollment updated successfully');
      setShowEditEnrollmentDialog(false);
      setEditingEnrollment(null);
      fetchParticipantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enrollment');
    } finally {
      setSavingEnrollment(false);
    }
  };

  const handleCloseEnrollment = async (enrollment) => {
    if (!window.confirm(`Are you sure you want to close this enrollment? This will mark it as closed but preserve all historical data.`)) {
      return;
    }
    
    setClosingEnrollment(true);
    try {
      await axios.post(`${API}/participants/${id}/enrollments/${enrollment.id}/close`);
      toast.success('Enrollment closed successfully');
      fetchParticipantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close enrollment');
    } finally {
      setClosingEnrollment(false);
    }
  };

  const handleReopenEnrollment = async (enrollment) => {
    if (!window.confirm(`Are you sure you want to reopen this enrollment?\n\nThis will also automatically reopen any associated cases.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/participants/${id}/enrollments/${enrollment.id}/reopen`);
      const casesReopened = response.data.cases_reopened || 0;
      if (casesReopened > 0) {
        toast.success(`Enrollment reopened successfully. ${casesReopened} case(s) also reopened.`);
      } else {
        toast.success('Enrollment reopened successfully');
      }
      fetchParticipantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen enrollment');
    }
  };

  const handleDeleteEnrollment = async (enrollment, cascade = false) => {
    const isAdmin = user?.role && ['system_admin', 'executive', 'operations_manager'].includes(user.role);
    
    if (cascade) {
      if (!window.confirm(`⚠️ CASCADE DELETE WARNING ⚠️\n\nThis will PERMANENTLY delete:\n• This enrollment\n• All associated cases\n• All case notes\n• All documents\n• All communications\n\nThis action CANNOT be undone. Are you absolutely sure?`)) {
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to delete this enrollment? This action cannot be undone.`)) {
        return;
      }
    }
    
    setDeletingEnrollment(true);
    try {
      const url = cascade 
        ? `${API}/participants/${id}/enrollments/${enrollment.id}?cascade=true`
        : `${API}/participants/${id}/enrollments/${enrollment.id}`;
      const response = await axios.delete(url);
      
      if (response.data.deleted) {
        const deleted = response.data.deleted;
        toast.success(`Enrollment deleted. Also removed: ${deleted.cases} case(s), ${deleted.case_notes} note(s), ${deleted.case_documents} document(s)`);
      } else {
        toast.success('Enrollment deleted successfully');
      }
      fetchParticipantData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete enrollment';
      
      // If there are dependencies and user is admin, offer cascade delete
      if (errorMsg.includes('dependencies') && isAdmin && !cascade) {
        if (window.confirm(`${errorMsg}\n\nAs an admin, you can cascade delete this enrollment and all its associated data. Do you want to proceed with cascade delete?`)) {
          handleDeleteEnrollment(enrollment, true);
          return;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setDeletingEnrollment(false);
    }
  };

  // Delete participant handler
  const handleDeleteParticipant = async () => {
    setDeletingParticipant(true);
    try {
      await axios.delete(`${API}/participants/${id}`);
      toast.success(`Participant ${participant.full_name} has been deleted`);
      navigate('/participants');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete participant');
      setDeletingParticipant(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatDateUtil(dateString) || '-';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'open': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper to check if a program type is SNAP Outreach (should not have case manager assignment)
  const isSnapOutreach = (programType) => {
    if (!programType) return false;
    const pt = programType.toLowerCase();
    return pt.includes('snap_outreach') || pt === 'snap outreach';
  };

  // Check if any cases in the list are NOT snap_outreach (to determine if we should show the Assigned To column)
  const hasNonSnapOutreachCases = cases.some(c => !isSnapOutreach(c.program_type));

  // Ban/Unban handlers
  const handleBanParticipant = async () => {
    if (!banReason.trim()) {
      toast.error('Please provide a reason for banning');
      return;
    }
    
    setBanning(true);
    try {
      const response = await axios.post(`${API}/participants/${id}/ban?reason=${encodeURIComponent(banReason)}`);
      toast.success(`Participant banned. ${response.data.enrollments_closed} enrollment(s) and ${response.data.cases_closed} case(s) closed.`);
      setShowBanDialog(false);
      setBanReason('');
      fetchParticipantData();
    } catch (error) {
      console.error('Failed to ban participant:', error);
      toast.error(error.response?.data?.detail || 'Failed to ban participant');
    } finally {
      setBanning(false);
    }
  };

  const handleUnbanParticipant = async () => {
    setUnbanning(true);
    try {
      await axios.post(`${API}/participants/${id}/unban`);
      toast.success('Participant ban removed. Enrollments and cases can now be reopened.');
      fetchParticipantData();
    } catch (error) {
      console.error('Failed to unban participant:', error);
      toast.error(error.response?.data?.detail || 'Failed to unban participant');
    } finally {
      setUnbanning(false);
    }
  };

  const canBanParticipants = ['system_admin', 'executive', 'operations_manager'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!participant) return null;

  const activeEnrollments = enrollments.filter(e => 
    e.enrollment_status === 'active' || e.status === 'open'
  );

  return (
    <div className="space-y-6" data-testid="participant-detail-page">
      {/* SNAP E&T Expiration Alert Modal - Blocks UI until acknowledged */}
      {showExpirationAlerts && (
        <ExpirationAlertModal
          participantId={id}
          participantName={participant.full_name}
          onComplete={() => setShowExpirationAlerts(false)}
        />
      )}
      
      {/* Pending Identification Verification Alert Modal - Blocks UI until acknowledged/bypassed */}
      {showPendingIdAlert && (
        <PendingIdentificationAlertModal
          participantId={id}
          participantName={participant.full_name}
          user={user}
          onClose={() => setShowPendingIdAlert(false)}
          onAcknowledged={() => {
            setShowPendingIdAlert(false);
            // Refresh data in case the note was unpinned
            fetchParticipantData();
          }}
        />
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/participants')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{participant.full_name}</h1>
              {participant.alias_name && (
                <span className="text-lg text-muted-foreground font-medium" data-testid="alias-name-display">
                  ({participant.alias_name})
                </span>
              )}
              {participant.is_banned && (
                <Badge 
                  variant="destructive" 
                  className="bg-black hover:bg-gray-900 text-white font-semibold px-3 py-1"
                  data-testid="banned-badge"
                >
                  <Ban className="w-4 h-4 mr-1" />
                  BANNED
                </Badge>
              )}
              {participant.out_of_service_area && (
                <Badge 
                  variant="destructive" 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-1"
                  data-testid="out-of-service-area-badge"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Out of Service Area
                </Badge>
              )}
              <Badge className={getStatusColor(participant.enrollment_status)}>
                {participant.enrollment_status}
              </Badge>
            </div>
            {/* SSN-based Participant ID with reveal/hide functionality */}
            <div className="flex items-center gap-2 mt-1">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground font-mono text-sm">
                ID: {ssnRevealed && revealedSSN ? revealedSSN : (participant.id_display || participant.ssn_masked || participant.id)}
              </span>
              <SsnIncompleteFlag participant={participant} onCompleted={() => { setSSNRevealed(false); setRevealedSSN(null); fetchParticipantData(); }} />
              {participant.ssn_masked && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={ssnRevealed ? handleHideSSN : handleRevealSSN}
                  disabled={revealingSSN}
                  data-testid="ssn-reveal-btn"
                >
                  {revealingSSN ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : ssnRevealed ? (
                    <>
                      <EyeOff className="w-3 h-3 mr-1" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Eye className="w-3 h-3 mr-1" />
                      Reveal
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="edit-btn">
            <Link to={`/participants/${id}/edit`}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Link>
          </Button>
          {canBanParticipants && !participant.is_banned && (
            <Button 
              variant="outline"
              onClick={() => setShowBanDialog(true)}
              className="text-black border-black hover:bg-black hover:text-white"
              data-testid="ban-participant-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Ban
            </Button>
          )}
          {canBanParticipants && participant.is_banned && (
            <Button 
              variant="outline"
              onClick={handleUnbanParticipant}
              disabled={unbanning}
              className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
              data-testid="unban-participant-btn"
            >
              {unbanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
              Remove Ban
            </Button>
          )}
          {canDelete && (
            <Button 
              variant="destructive" 
              onClick={() => setShowDeleteParticipantDialog(true)}
              data-testid="delete-participant-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="enroll-btn">
                <ClipboardList className="w-4 h-4 mr-2" />
                Enroll in Program
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Enroll in Program</DialogTitle>
                <DialogDescription>
                  Enroll {participant.full_name} in a new program. A case will be automatically created.
                </DialogDescription>
              </DialogHeader>
              <DialogBody className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Enrollment Type *</Label>
                  <Select 
                    value={enrollmentForm.program_type} 
                    onValueChange={handleEnrollmentTypeChange}
                  >
                    <SelectTrigger data-testid="enrollment-type-select">
                      <SelectValue placeholder="Select enrollment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {programTypes.filter(pt => pt.is_active !== false).map((pt) => (
                        <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {enrollmentForm.program_type && (
                  <div className="p-3 rounded-lg bg-muted text-sm">
                    <p className="text-muted-foreground">State: <span className="font-medium text-foreground">
                      {programTypes.find(pt => pt.id === enrollmentForm.program_type)?.state || 'N/A'}
                    </span></p>
                    <p className="text-xs text-muted-foreground mt-1">
                      A case will be automatically created for this enrollment.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Contract</Label>
                  <Select 
                    value={enrollmentForm.contract_name || '__none__'} 
                    onValueChange={(val) => {
                      const actualVal = val === '__none__' ? '' : val;
                      const contract = contracts.find(c => c.name === actualVal);
                      setEnrollmentForm(prev => ({ 
                        ...prev, 
                        contract_name: actualVal,
                        funding_source: contract?.funding_source || prev.funding_source
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="contract-type-select">
                      <SelectValue placeholder="Select contract (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No Contract</SelectItem>
                      {/* Filter contracts by selected enrollment type and state */}
                      {contracts
                        .filter(c => {
                          if (!enrollmentForm.program_type) return true;
                          // Extract base program type from enrollment type ID (e.g., snap_et_tn -> snap_et, food_pantry_tn -> food_pantry)
                          const baseProgramType = enrollmentForm.program_type.replace(/_[a-z]{2}$/, '');
                          // Map to contract program type (e.g., food_pantry -> food_assistance)
                          const contractProgramType = ENROLLMENT_TO_CONTRACT_PROGRAM_MAP[baseProgramType] || baseProgramType;
                          return c.program_types && c.program_types.some(pt => 
                            pt === contractProgramType || pt === baseProgramType || pt === enrollmentForm.program_type
                          );
                        })
                        .filter(c => {
                          const selectedProgramType = programTypes.find(pt => pt.id === enrollmentForm.program_type);
                          return !selectedProgramType?.state || c.state === selectedProgramType.state;
                        })
                        .map((c) => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {loadingDefaults && (
                    <p className="text-xs text-muted-foreground">Loading defaults...</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Funding Source</Label>
                  <Select 
                    value={enrollmentForm.funding_source}
                    onValueChange={(val) => setEnrollmentForm(prev => ({ ...prev, funding_source: val }))}
                  >
                    <SelectTrigger data-testid="funding-source-select">
                      <SelectValue placeholder="Select funding source" />
                    </SelectTrigger>
                    <SelectContent>
                      {fundingSources.filter(fs => fs.is_active !== false).map((fs) => (
                        <SelectItem key={fs.id} value={fs.name}>{fs.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input 
                      type="date"
                      value={enrollmentForm.start_date}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input 
                      type="date"
                      value={enrollmentForm.end_date}
                      onChange={(e) => setEnrollmentForm(prev => ({ ...prev, end_date: e.target.value }))}
                      data-testid="end-date-input"
                    />
                    {enrollmentForm.end_date && (
                      <p className="text-xs text-muted-foreground">Auto-calculated based on program</p>
                    )}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEnrollDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateEnrollment} disabled={enrolling}>
                  {enrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Enrollment
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Banned Alert */}
      {participant.is_banned && (
        <Card className="border-2 border-black bg-black/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                <Ban className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-black mb-1">Participant Banned</h3>
                <p className="text-gray-700 mb-2">
                  This participant has been banned and all enrollments/cases have been closed.
                </p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Reason:</strong> {participant.ban_reason || 'No reason provided'}</p>
                  <p><strong>Banned by:</strong> {participant.banned_by_name || 'Unknown'}</p>
                <p><strong>Banned on:</strong> {participant.banned_at ? formatDateUtil(participant.banned_at) : 'Unknown'}</p>
                </div>
                {canBanParticipants && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleUnbanParticipant}
                    disabled={unbanning}
                    className="mt-3 text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                  >
                    {unbanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                    Remove Ban
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date of Birth</p>
                <p className="font-medium">
                  {formatDateUtil(participant.date_of_birth)}
                  {calculateAge(participant.date_of_birth) !== null && (
                    <span className="text-muted-foreground ml-2">({calculateAge(participant.date_of_birth)} years old)</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Intake Date</p>
                <p className="font-medium">
                  {(() => {
                    // Get the earliest enrollment date
                    const allDates = enrollments
                      .map(e => e.start_date || e.open_date || e.created_at)
                      .filter(d => d)
                      .sort((a, b) => new Date(a) - new Date(b));
                    
                    if (allDates.length > 0) {
                      return formatDateUtil(allDates[0]) || 'No enrollments';
                    }
                    return 'No enrollments';
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Enrollments</p>
                <p className="font-medium">{activeEnrollments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Cases</p>
                <p className="font-medium">{cases.filter(c => c.status === 'open').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-overview">Profile Details</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="cases" data-testid="tab-cases">Cases ({cases.length})</TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({notes.length})</TabsTrigger>
        </TabsList>

        {/* Program Enrollments Tab */}
        <TabsContent value="enrollments" className="space-y-4">
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Program Enrollments</CardTitle>
                <CardDescription>
                  Manage program enrollments. All users can edit, close, or delete enrollments.
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowEnrollDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Enrollment
              </Button>
            </CardHeader>
            <CardContent>
              {enrollments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Program</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">State</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Contract</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Start Date</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">End Date</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Modified</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Close Date</th>
                        <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.map((enrollment) => {
                        const isOpen = enrollment.status === 'open' || enrollment.enrollment_status === 'active';
                        const caseCount = cases.filter(c => c.enrollment_id === enrollment.id).length;
                        
                        return (
                          <tr key={enrollment.id} className="border-b border-border hover:bg-muted/30">
                            <td className="py-3 px-3">
                              <div>
                                <p className="font-medium">{enrollment.program_name}</p>
                                <p className="text-xs text-muted-foreground font-mono">{enrollment.id}</p>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <Badge className={getStatusColor(enrollment.status || enrollment.enrollment_status)}>
                                {(enrollment.status || enrollment.enrollment_status) === 'open' || 
                                 (enrollment.status || enrollment.enrollment_status) === 'active' 
                                  ? 'Open' : 'Closed'}
                              </Badge>
                              {caseCount > 0 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  ({caseCount} case{caseCount !== 1 ? 's' : ''})
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-sm">{enrollment.state}</td>
                            <td className="py-3 px-3 text-sm">{enrollment.contract_name || '-'}</td>
                            <td className="py-3 px-3 text-sm">{formatDate(enrollment.start_date)}</td>
                            <td className="py-3 px-3 text-sm">{formatDate(enrollment.end_date)}</td>
                            <td className="py-3 px-3 text-sm">{formatDate(enrollment.modified_date || enrollment.updated_at)}</td>
                            <td className="py-3 px-3 text-sm">{formatDate(enrollment.close_date)}</td>
                            <td className="py-3 px-3">
                              <div className="flex justify-end gap-1">
                                {isOpen && (
                                  <Button 
                                    size="sm" 
                                    variant="default"
                                    onClick={() => openCaseDialog(enrollment)}
                                    title="Open new case under this enrollment"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openEditEnrollmentDialog(enrollment)}
                                  title="Edit enrollment"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {isOpen ? (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleCloseEnrollment(enrollment)}
                                    disabled={closingEnrollment}
                                    title="Close enrollment"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  // Reopen button only visible to System Admin, Executive, Operations Manager
                                  ['system_admin', 'executive', 'operations_manager'].includes(user?.role) && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleReopenEnrollment(enrollment)}
                                      title="Reopen enrollment and associated cases"
                                      className="border-green-300 hover:border-green-500 hover:bg-green-50"
                                    >
                                      <RefreshCw className="w-4 h-4 text-green-600" />
                                    </Button>
                                  )
                                )}
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleDeleteEnrollment(enrollment)}
                                  disabled={deletingEnrollment}
                                  title="Delete enrollment (only if no cases)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No program enrollments yet</p>
                  <Button className="mt-4" onClick={() => setShowEnrollDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Enrollment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cases Tab */}
        <TabsContent value="cases">
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Case History</CardTitle>
              {activeEnrollments.length > 0 && (
                <Select onValueChange={(val) => {
                  const enrollment = enrollments.find(e => e.id === val);
                  if (enrollment) openCaseDialog(enrollment);
                }}>
                  <SelectTrigger className="w-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    <span>New Case</span>
                  </SelectTrigger>
                  <SelectContent>
                    {activeEnrollments.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.program_name} ({e.state})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CardHeader>
            <CardContent>
              {cases.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="cases-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Case ID</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Service Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Program</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Start Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">End Date</th>
                        {hasNonSnapOutreachCases && (
                          <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Assigned To</th>
                        )}
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map((caseItem) => {
                        // Find the corresponding enrollment to get dates
                        const enrollment = enrollments.find(e => e.id === caseItem.enrollment_id);
                        return (
                          <tr key={caseItem.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                            <td className="py-3 px-4 text-sm font-mono">{caseItem.id}</td>
                            <td className="py-3 px-4 text-sm">{caseItem.service_type_name || caseItem.service_type?.replace(/_/g, ' ')}</td>
                            <td className="py-3 px-4 text-sm">
                              <div>{caseItem.program_name}</div>
                              <div className="text-xs text-muted-foreground">{caseItem.state}</div>
                            </td>
                            <td className="py-3 px-4 text-sm">{formatDate(enrollment?.start_date || caseItem.start_date)}</td>
                            <td className="py-3 px-4 text-sm">{formatDate(enrollment?.end_date || caseItem.end_date)}</td>
                            {hasNonSnapOutreachCases && (
                              <td className="py-3 px-4 text-sm">
                                {isSnapOutreach(caseItem.program_type) ? '-' : (caseItem.assigned_staff_name || 'Unassigned')}
                              </td>
                            )}
                            <td className="py-3 px-4">
                              <Badge className={getStatusColor(caseItem.status)}>{caseItem.status}</Badge>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Button variant="ghost" size="sm" asChild>
                                <Link to={`/cases/${caseItem.id}`}>View</Link>
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No cases found</p>
                  {activeEnrollments.length > 0 ? (
                    <p className="text-sm mt-2">Select an enrollment above to open a new case</p>
                  ) : (
                    <p className="text-sm mt-2">Create a program enrollment first to open cases</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications">
          <CommunicationsPanel
            participantId={participant.id}
            participantName={participant.full_name}
            participantPhone={participant.phone}
            participantEmail={participant.email}
            cases={cases}
            onCommunicationSent={fetchParticipantData}
          />
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card className="card-shadow">
            <CardHeader>
              <div>
                <CardTitle className="text-lg">Case Notes</CardTitle>
                <CardDescription>All notes across this participant&apos;s cases. To add a note, open a specific case.</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {notes.length > 0 ? (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div key={note.id} className="p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Subject Line */}
                          <h4 className="font-semibold text-base mb-2">
                            {note.subject || 'Untitled Note'}
                          </h4>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline" className="capitalize">{note.note_type?.replace(/_/g, ' ')}</Badge>
                            <Badge className={`status-${note.status}`}>{note.status}</Badge>
                            {note.is_system_generated && (
                              <Badge variant="secondary" className="text-xs">Auto-generated</Badge>
                            )}
                            <span className="text-sm text-muted-foreground">
                              {formatDateUtil(note.date_of_service || note.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{note.content}</p>
                        </div>
                        {/* View/Edit/Delete Actions */}
                        <div className="flex gap-1 shrink-0">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/cases/${note.case_id}`, { state: { activeTab: 'notes' } })}
                            title="View in Case"
                            data-testid={`view-note-${note.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/case-notes/${note.id}/edit`)}
                            title="Edit note"
                            data-testid={`edit-note-${note.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        By {note.author_name} • Case: <span 
                          className="text-primary cursor-pointer hover:underline"
                          onClick={() => navigate(`/cases/${note.case_id}`, { state: { activeTab: 'notes' } })}
                        >{note.case_id}</span>
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No notes found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Info Tab - Redesigned with two-column layout */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Profile Information */}
            <div className="space-y-4">
              {/* Identification Information */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold">Identification</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>View</Link>
                  </Button>
                </div>
                <div className="space-y-3 pl-13 ml-[52px]">
                  {/* SSN - Always shown masked with last 4 digits */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">SSN:</span>
                      <span className="font-mono font-medium">
                        {ssnRevealed && revealedSSN 
                          ? revealedSSN 
                          : `***-**-${participant.ssn_last_four || '****'}`}
                      </span>
                      <SsnIncompleteFlag participant={participant} onCompleted={() => { setSSNRevealed(false); setRevealedSSN(null); fetchParticipantData(); }} testId="ssn-incomplete-flag-info" />
                    </div>
                    {(participant.ssn_masked || participant.ssn_last_four) && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={ssnRevealed ? handleHideSSN : handleRevealSSN}
                        disabled={revealingSSN}
                      >
                        {revealingSSN ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : ssnRevealed ? (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Hide
                          </>
                        ) : (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Reveal
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  {/* State ID */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">State ID:</span>
                    <span className="font-medium">
                      {participant.state_id || <span className="text-muted-foreground italic">Not provided</span>}
                    </span>
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-semibold">Demographics</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>Edit</Link>
                  </Button>
                </div>
                <div className="space-y-2 pl-13 ml-[52px]">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Alias Name:</span>
                    <span className="font-medium" data-testid="profile-alias-name">
                      {participant.alias_name || <span className="text-muted-foreground italic">Not specified</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Gender:</span>
                    <span className="font-medium">
                      {participant.gender || <span className="text-muted-foreground italic">Not specified</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Race:</span>
                    <span className="font-medium">
                      {participant.race || <span className="text-muted-foreground italic">Not specified</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Primary Language:</span>
                    <span className="font-medium">
                      {participant.primary_language || 'English'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-semibold">Contact Information</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>Edit</Link>
                  </Button>
                </div>
                <div className="space-y-2 pl-13 ml-[52px]">
                  {participant.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${participant.email}`} className="text-primary hover:underline">
                        {participant.email}
                      </a>
                    </div>
                  )}
                  {participant.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${participant.phone}`} className="hover:underline">
                        {participant.phone}
                      </a>
                    </div>
                  )}
                  {/* Address - Always shown inline */}
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    {participant.is_homeless ? (
                      <span className="text-amber-600 font-medium">Currently experiencing homelessness</span>
                    ) : (participant.address_line1 || participant.address) ? (
                      <div>
                        <span>{participant.address_line1 || participant.address}</span>
                        {participant.address_line2 && <span>, {participant.address_line2}</span>}
                        <br />
                        <span>
                          {participant.city && `${participant.city}, `}
                          {participant.state && `${participant.state} `}
                          {participant.zip_code}
                        </span>
                        {participant.county && (
                          <>
                            <br />
                            <span className="text-muted-foreground">{participant.county}</span>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic">No address on file</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <h3 className="font-semibold">Emergency Contact</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>Edit</Link>
                  </Button>
                </div>
                <div className="space-y-1 pl-13 ml-[52px] text-sm">
                  {participant.emergency_contact?.name ? (
                    <>
                      <p className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {participant.emergency_contact.name}
                        {participant.emergency_contact.relationship && (
                          <span className="text-muted-foreground">({participant.emergency_contact.relationship})</span>
                        )}
                      </p>
                      {participant.emergency_contact.phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          {participant.emergency_contact.phone}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground">No emergency contact on file</p>
                  )}
                </div>
              </div>

              {/* Income Information */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="font-semibold">Income Information</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>Edit</Link>
                  </Button>
                </div>
                <div className="space-y-1 pl-13 ml-[52px] text-sm">
                  {participant.income_info ? (
                    <>
                      <p className="text-muted-foreground">
                        Household Size: <span className="text-foreground font-medium">{participant.income_info.household_size || 'Not specified'}</span>
                      </p>
                      <p className="text-muted-foreground">
                        Monthly Income: <span className="text-foreground font-medium">
                          {participant.income_info.monthly_income != null 
                            ? `$${participant.income_info.monthly_income.toLocaleString()}.00`
                            : 'Not specified'}
                        </span>
                      </p>
                    </>
                  ) : (
                    <p className="text-muted-foreground">No income information on file</p>
                  )}
                </div>
              </div>

              {/* Disabilities */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Accessibility className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-semibold">Disabilities</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>View</Link>
                  </Button>
                </div>
                <div className="pl-13 ml-[52px] text-sm">
                  {participant.disabilities && participant.disabilities.length > 0 ? (
                    <p className="text-muted-foreground">
                      {participant.disabilities.map(d => {
                        const labels = {
                          none: 'None reported',
                          visual: 'Visual Impairment',
                          hearing: 'Hearing Impairment',
                          cognitive: 'Cognitive/Learning',
                          physical: 'Physical/Mobility',
                          mental_health: 'Mental Health',
                          chronic_health: 'Chronic Health',
                          other: 'Other'
                        };
                        return labels[d] || d;
                      }).join(', ')}
                    </p>
                  ) : (
                    <p className="text-muted-foreground">No disabilities reported</p>
                  )}
                </div>
              </div>

              {/* Barriers to Employment */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                      <FolderOpen className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="font-semibold">Barriers to Employment</h3>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${id}/edit`}>View</Link>
                  </Button>
                </div>
                <div className="pl-13 ml-[52px] text-sm">
                  {participant.barriers_to_employment && participant.barriers_to_employment.length > 0 ? (
                    <p className="text-muted-foreground">
                      {participant.barriers_to_employment.map(b => {
                        const labels = {
                          no_hs_diploma: 'No High School Diploma',
                          disability: 'Disability',
                          childcare: 'Childcare',
                          illiteracy: 'Illiteracy',
                          higher_ed_requirements: 'Higher Education Requirements',
                          criminal_history: 'Criminal History',
                          poor_work_history: 'Limited work history',
                          english_proficiency: 'English Proficiency',
                          transportation: 'Transportation'
                        };
                        return labels[b] || b;
                      }).join(', ')}
                    </p>
                  ) : (
                    <p className="text-muted-foreground">No barriers reported</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Recent Activity */}
            <div className="bg-card rounded-lg border border-border">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Recent Activity</h3>
                  <div className="flex gap-2">
                    <Select defaultValue="all">
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue placeholder="All Activity" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Activity</SelectItem>
                        <SelectItem value="notes">Case Notes</SelectItem>
                        <SelectItem value="communications">Communications</SelectItem>
                        <SelectItem value="documents">Documents</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div className="divide-y divide-border">
                {loadingActivities ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : activities.length > 0 ? (
                  <>
                    {activities.slice(0, 10).map((activity, index) => {
                      // Get icon and color based on activity type
                      const getActivityStyle = () => {
                        const action = activity.action || '';
                        if (action.includes('note')) {
                          return { bg: 'bg-orange-100 dark:bg-orange-900/30', color: 'text-orange-600' };
                        }
                        if (action.includes('call') || action.includes('sms') || action.includes('email')) {
                          return { bg: 'bg-green-100 dark:bg-green-900/30', color: 'text-green-600' };
                        }
                        if (action.includes('document') || action.includes('upload')) {
                          return { bg: 'bg-purple-100 dark:bg-purple-900/30', color: 'text-purple-600' };
                        }
                        if (action.includes('case') || action.includes('appointment')) {
                          return { bg: 'bg-yellow-100 dark:bg-yellow-900/30', color: 'text-yellow-600' };
                        }
                        return { bg: 'bg-blue-100 dark:bg-blue-900/30', color: 'text-blue-600' };
                      };
                      
                      const getActivityIcon = () => {
                        const action = activity.action || '';
                        if (action.includes('note')) return <FileText className="w-4 h-4" />;
                        if (action.includes('call')) return <Phone className="w-4 h-4" />;
                        if (action.includes('sms') || action.includes('message')) return <MessageSquare className="w-4 h-4" />;
                        if (action.includes('email')) return <Mail className="w-4 h-4" />;
                        if (action.includes('document') || action.includes('upload')) return <Upload className="w-4 h-4" />;
                        if (action.includes('case')) return <FolderOpen className="w-4 h-4" />;
                        return <Activity className="w-4 h-4" />;
                      };
                      
                      const style = getActivityStyle();
                      
                      // Get badge for status
                      const getBadge = () => {
                        const action = activity.action || '';
                        if (action.includes('deleted')) return <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Deleted</Badge>;
                        if (action.includes('completed')) return <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-300 text-green-600">Completed</Badge>;
                        if (action.includes('sent') || action.includes('outbound')) return <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-600">Delivered</Badge>;
                        if (activity.details?.status_change) return <Badge variant="outline" className="text-[10px] px-1.5 py-0">{activity.details.status_change}</Badge>;
                        return null;
                      };
                      
                      // Format timestamp
                      const formatTime = (ts) => {
                        if (!ts) return '';
                        const date = new Date(ts);
                        const now = new Date();
                        const isToday = date.toDateString() === now.toDateString();
                        const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                        if (isToday) {
                          return `Today at ${timeStr}`;
                        }
                        return `${formatDateUtil(ts)} at ${timeStr}`;
                      };
                      
                      // Handle click to navigate to the relevant item
                      const handleActivityClick = () => {
                        const action = activity.action || '';
                        const details = activity.details || {};
                        
                        // Case note actions - navigate to case with notes tab
                        if (action.includes('case_note') || action.includes('note_')) {
                          if (activity.case_id || details.case_id) {
                            navigate(`/cases/${activity.case_id || details.case_id}`, { state: { activeTab: 'notes' } });
                            return;
                          }
                        }
                        
                        // Case actions - navigate to case
                        if (action.includes('case_') && (activity.case_id || details.case_id)) {
                          navigate(`/cases/${activity.case_id || details.case_id}`);
                          return;
                        }
                        
                        // Enrollment actions - stay on participant page, switch to enrollments tab
                        if (action.includes('enrollment')) {
                          // Scroll to enrollments section or switch tab
                          const enrollmentsTab = document.querySelector('[data-testid="enrollments-tab"]');
                          if (enrollmentsTab) enrollmentsTab.click();
                          return;
                        }
                        
                        // Communication/SMS actions - navigate to case with communications tab
                        if (action.includes('sms') || action.includes('call') || action.includes('email') || action.includes('communication')) {
                          if (activity.case_id || details.case_id) {
                            navigate(`/cases/${activity.case_id || details.case_id}`, { state: { activeTab: 'communications' } });
                            return;
                          }
                        }
                        
                        // Document actions - navigate to case with documents tab
                        if (action.includes('document')) {
                          if (activity.case_id || details.case_id) {
                            navigate(`/cases/${activity.case_id || details.case_id}`, { state: { activeTab: 'documents' } });
                            return;
                          }
                        }
                        
                        // Appointment actions - navigate to case
                        if (action.includes('appointment')) {
                          if (activity.case_id || details.case_id) {
                            navigate(`/cases/${activity.case_id || details.case_id}`);
                            return;
                          }
                        }
                        
                        // Default - if there's a case_id, go to the case
                        if (activity.case_id || details.case_id) {
                          navigate(`/cases/${activity.case_id || details.case_id}`);
                        }
                      };
                      
                      return (
                        <div 
                          key={activity.id || index} 
                          className="flex items-start gap-3 p-4 hover:bg-muted/50 transition-colors cursor-pointer group"
                          onClick={handleActivityClick}
                        >
                          <div className={`w-9 h-9 rounded-full ${style.bg} ${style.color} flex items-center justify-center shrink-0`}>
                            {getActivityIcon()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{activity.description}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {activity.case_name && (
                                <span className="text-primary font-medium">{activity.case_name} • </span>
                              )}
                              {activity.details?.program_name || activity.details?.case_type || ''}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <span>{activity.user_name}</span>
                              <span>•</span>
                              <span>{formatTime(activity.timestamp)}</span>
                              {getBadge()}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      );
                    })}
                    
                    {activities.length > 10 && (
                      <div className="p-4 text-center">
                        <Button variant="outline" size="sm">
                          View All Activity
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Case Creation Dialog */}
      <Dialog open={showCaseDialog} onOpenChange={setShowCaseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Open New Case</DialogTitle>
            <DialogDescription>
              {selectedEnrollment && (
                <>
                  Create a case under <strong>{selectedEnrollment.program_name}</strong> ({selectedEnrollment.state})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedEnrollment && (
              <div className="p-3 rounded-lg bg-muted text-sm">
                <p><strong>Program:</strong> {selectedEnrollment.program_name}</p>
                <p><strong>State:</strong> {selectedEnrollment.state}</p>
                {selectedEnrollment.contract_name && <p><strong>Contract:</strong> {selectedEnrollment.contract_name}</p>}
                {selectedEnrollment.funding_source && <p><strong>Funding:</strong> {selectedEnrollment.funding_source}</p>}
              </div>
            )}
            <div className="space-y-2">
              <Label>Service Type *</Label>
              {loadingServiceTypes ? (
                <div className="flex items-center gap-2 p-3 border rounded-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading allowed service types...</span>
                </div>
              ) : (
                <>
                  <Select 
                    value={caseForm.service_type} 
                    onValueChange={(val) => setCaseForm(prev => ({ ...prev, service_type: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedServiceTypes.map((st) => (
                        <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only service types allowed for {selectedEnrollment?.program_name || 'this program'} are shown
                  </p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={caseForm.priority} 
                onValueChange={(val) => setCaseForm(prev => ({ ...prev, priority: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Outcomes</Label>
              <Input 
                value={caseForm.target_outcomes}
                onChange={(e) => setCaseForm(prev => ({ ...prev, target_outcomes: e.target.value }))}
                placeholder="e.g., Job placement within 90 days"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input 
                value={caseForm.notes}
                onChange={(e) => setCaseForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Initial case notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCaseDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCase} disabled={creatingCase || !caseForm.service_type}>
              {creatingCase ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Enrollment Dialog */}
      <Dialog open={showEditEnrollmentDialog} onOpenChange={setShowEditEnrollmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Enrollment</DialogTitle>
            <DialogDescription>
              {editingEnrollment && (
                <>
                  Modify enrollment details for <strong>{editingEnrollment.program_name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingEnrollment && (
              <div className="p-3 rounded-lg bg-muted text-sm">
                <p><strong>Enrollment ID:</strong> <span className="font-mono">{editingEnrollment.id}</span></p>
                <p><strong>Status:</strong> {editingEnrollment.status || editingEnrollment.enrollment_status}</p>
                <p><strong>Created:</strong> {formatDate(editingEnrollment.open_date || editingEnrollment.created_at)}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Program Type *</Label>
              <Select 
                value={enrollmentEditForm.program_type} 
                onValueChange={(val) => setEnrollmentEditForm(prev => ({ ...prev, program_type: val }))}
              >
                <SelectTrigger data-testid="edit-enrollment-program-type">
                  <SelectValue placeholder="Select program" />
                </SelectTrigger>
                <SelectContent>
                  {programTypes.map((pt) => (
                    <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>State *</Label>
              <Select 
                value={enrollmentEditForm.state} 
                onValueChange={(val) => setEnrollmentEditForm(prev => ({ ...prev, state: val }))}
              >
                <SelectTrigger data-testid="edit-enrollment-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contract</Label>
              <Select 
                value={enrollmentEditForm.contract_name || '__none__'} 
                onValueChange={(val) => {
                  const actualVal = val === '__none__' ? '' : val;
                  const contract = contracts.find(c => c.name === actualVal);
                  setEnrollmentEditForm(prev => ({ 
                    ...prev, 
                    contract_name: actualVal,
                    funding_source: contract?.funding_source || prev.funding_source
                  }));
                }}
              >
                <SelectTrigger data-testid="edit-enrollment-contract">
                  <SelectValue placeholder="Select contract (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Contract</SelectItem>
                  {contracts
                    .filter(c => {
                      if (!enrollmentEditForm.program_type) return true;
                      // Extract base program type from enrollment type ID (e.g., snap_et_tn -> snap_et, food_pantry_tn -> food_pantry)
                      const baseProgramType = enrollmentEditForm.program_type.replace(/_[a-z]{2}$/, '');
                      // Map to contract program type (e.g., food_pantry -> food_assistance)
                      const contractProgramType = ENROLLMENT_TO_CONTRACT_PROGRAM_MAP[baseProgramType] || baseProgramType;
                      return c.program_types && c.program_types.some(pt => 
                        pt === contractProgramType || pt === baseProgramType || pt === enrollmentEditForm.program_type
                      );
                    })
                    .filter(c => !enrollmentEditForm.state || c.state === enrollmentEditForm.state)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input 
                type="date"
                value={enrollmentEditForm.start_date || ''}
                onChange={(e) => setEnrollmentEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                data-testid="edit-enrollment-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input 
                type="date"
                value={enrollmentEditForm.end_date || ''}
                onChange={(e) => setEnrollmentEditForm(prev => ({ ...prev, end_date: e.target.value }))}
                data-testid="edit-enrollment-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Funding Source</Label>
              <Input 
                value={enrollmentEditForm.funding_source || ''}
                onChange={(e) => setEnrollmentEditForm(prev => ({ ...prev, funding_source: e.target.value }))}
                placeholder="e.g., USDA SNAP, HUD"
                data-testid="edit-enrollment-funding"
              />
            </div>
            {/* Hide Assigned Staff field for SNAP Outreach enrollments */}
            {!isSnapOutreach(editingEnrollment?.program_type) && (
              <div className="space-y-2">
                <Label>Assigned Staff ID</Label>
                <Input 
                  value={enrollmentEditForm.assigned_staff_id || ''}
                  onChange={(e) => setEnrollmentEditForm(prev => ({ ...prev, assigned_staff_id: e.target.value }))}
                  placeholder="Staff member ID"
                  data-testid="edit-enrollment-staff"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input 
                value={enrollmentEditForm.notes || ''}
                onChange={(e) => setEnrollmentEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                data-testid="edit-enrollment-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditEnrollmentDialog(false);
                setEditingEnrollment(null);
              }}
              data-testid="edit-enrollment-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateEnrollment} 
              disabled={savingEnrollment}
              data-testid="edit-enrollment-save-btn"
            >
              {savingEnrollment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Participant Dialog */}
      <Dialog open={showDeleteParticipantDialog} onOpenChange={setShowDeleteParticipantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Participant
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete the participant and all their related data including enrollments, cases, notes, documents, and communications.
            </DialogDescription>
          </DialogHeader>
          
          {participant && (
            <div className="py-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{participant.full_name}</p>
                <p className="text-sm text-muted-foreground font-mono">{participant.id}</p>
                {enrollments.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {enrollments.length} enrollment(s), {cases.length} case(s)
                  </p>
                )}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  Warning: This action cannot be undone!
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteParticipantDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteParticipant}
              disabled={deletingParticipant}
              data-testid="confirm-delete-participant-btn"
            >
              {deletingParticipant ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Participant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban Participant Dialog */}
      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-black">
              <Ban className="w-5 h-5" />
              Ban Participant
            </DialogTitle>
            <DialogDescription>
              Banning this participant will immediately close all their open enrollments and cases.
              This action can be reversed later.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800 font-medium">
                Warning: This will close {activeEnrollments.length} enrollment(s) and {cases.filter(c => c.status === 'open').length} open case(s).
              </p>
            </div>
            <div className="space-y-2">
              <Label>Reason for Ban *</Label>
              <Textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Please provide a reason for banning this participant..."
                rows={3}
                data-testid="ban-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBanDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBanParticipant} 
              disabled={banning || !banReason.trim()}
              className="bg-black hover:bg-gray-900 text-white"
              data-testid="confirm-ban-btn"
            >
              {banning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Banning...
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4 mr-2" />
                  Ban Participant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParticipantDetailPage;
